import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserCircleIcon,
  KeyIcon,
  TrashIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { type ThemePreference, useTheme } from '../../contexts/ThemeContext';
import { authApi } from '../../services/api';
import { updatePassword, deleteUser } from '@aws-amplify/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ProfilePreferencesForm,
  type ProfileFormData,
  type PreferencesFormData,
} from '../../components/ProfilePreferencesForm';

export const SETTINGS_TAB_KEYS = ['appearance', 'profile', 'password', 'account'] as const;
export type SettingsTabKey = (typeof SETTINGS_TAB_KEYS)[number];

function isValidTab(tab: string): tab is SettingsTabKey {
  return SETTINGS_TAB_KEYS.includes(tab as SettingsTabKey);
}

const EMPTY_PREFS: PreferencesFormData = {
  care_settings: [],
  billing_types: [],
  rcm_departments: [],
  payer_focus: [],
  interest_areas: [],
  role_level: '',
  primary_state: '',
  mac_region: '',
  additional_states: [],
};

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTab?: SettingsTabKey;
}

export function SettingsDialog({ open, onOpenChange, initialTab = 'appearance' }: SettingsDialogProps) {
  const { user, refreshAuth, logout } = useAuth();
  const navigate = useNavigate();
  const { themePreference, resolvedTheme, setThemePreference } = useTheme();

  const [activeTab, setActiveTab] = useState<SettingsTabKey>(initialTab);

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);

  // ── Profile state ──
  const [profile, setProfile] = useState<ProfileFormData>({
    full_name: user?.full_name ?? '',
    organization: user?.organization ?? '',
    organization_type: user?.organization_type ?? '',
    title: user?.title ?? '',
  });
  const [preferences, setPreferences] = useState<PreferencesFormData>(EMPTY_PREFS);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ── Password state ──
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ── Delete state ──
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (user) {
      setProfile({
        full_name: user.full_name ?? '',
        organization: user.organization ?? '',
        organization_type: user.organization_type ?? '',
        title: user.title ?? '',
      });
    }
  }, [user]);

  useEffect(() => {
    if (open && activeTab === 'profile' && !prefsLoaded) {
      authApi.getPreferences().then((prefs) => {
        setPreferences({
          care_settings: prefs.care_settings ?? [],
          billing_types: prefs.billing_types ?? [],
          rcm_departments: prefs.rcm_departments ?? [],
          payer_focus: prefs.payer_focus ?? [],
          interest_areas: prefs.interest_areas ?? [],
          role_level: prefs.role_level ?? '',
          primary_state: prefs.primary_state ?? '',
          mac_region: prefs.mac_region ?? '',
          additional_states: prefs.additional_states ?? [],
        });
        setPrefsLoaded(true);
      }).catch(() => {
        setPrefsLoaded(true);
      });
    }
  }, [open, activeTab, prefsLoaded]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileMessage(null);
    setProfileSaving(true);
    try {
      await authApi.updateProfile({
        full_name: profile.full_name || undefined,
        organization: profile.organization || undefined,
        organization_type: profile.organization_type || undefined,
        title: profile.title || undefined,
      });
      await authApi.updatePreferences({
        care_settings: preferences.care_settings,
        billing_types: preferences.billing_types,
        rcm_departments: preferences.rcm_departments,
        payer_focus: preferences.payer_focus,
        interest_areas: preferences.interest_areas,
        role_level: preferences.role_level || undefined,
        primary_state: preferences.primary_state || undefined,
        mac_region: preferences.mac_region || undefined,
        additional_states: preferences.additional_states,
      });
      await refreshAuth();
      setProfileMessage({ type: 'success', text: 'Profile updated.' });
    } catch (err: unknown) {
      const detail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : 'Failed to update profile';
      setProfileMessage({
        type: 'error',
        text: Array.isArray(detail) ? detail.join(', ') : String(detail),
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    setPasswordSaving(true);
    try {
      await updatePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
      });
      setPasswordMessage({ type: 'success', text: 'Password updated.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      setPasswordMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to change password',
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setDeleteMessage(null);
    if (deleteConfirm !== 'delete my account') {
      setDeleteMessage({
        type: 'error',
        text: 'Please type "delete my account" to confirm.',
      });
      return;
    }
    setDeleteSaving(true);
    try {
      await authApi.deleteAccount();
      try {
        await deleteUser();
      } catch (cognitoErr: unknown) {
        console.warn('Cognito deleteUser failed:', cognitoErr instanceof Error ? cognitoErr.message : cognitoErr);
      }
      onOpenChange(false);
      await logout();
      navigate('/login', { replace: true });
    } catch (err: unknown) {
      setDeleteMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to delete account',
      });
    } finally {
      setDeleteSaving(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] max-h-[90vh] !flex !flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="flex-shrink-0 px-6 py-5 border-b border-border bg-muted/30 space-y-1">
          <DialogTitle className="text-xl font-semibold">Settings</DialogTitle>
          <DialogDescription>
            Manage your profile, password, and account preferences.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          <div className="px-6 py-6">
            <Tabs
              value={activeTab}
              onValueChange={(value) => {
                if (isValidTab(value)) setActiveTab(value);
              }}
            >
              <TabsList className="grid w-full grid-cols-4 mb-6">
                <TabsTrigger value="appearance">Appearance</TabsTrigger>
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="password">Password</TabsTrigger>
                <TabsTrigger value="account">Account</TabsTrigger>
              </TabsList>

              <TabsContent value="appearance" className="mt-0">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <MoonIcon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Appearance</CardTitle>
                        <CardDescription className="mt-1">
                          Theme preference (currently {resolvedTheme})
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="themePreference" className="text-sm font-medium">
                        Theme
                      </Label>
                      <Select
                        value={themePreference}
                        onValueChange={(value) => setThemePreference(value as ThemePreference)}
                      >
                        <SelectTrigger id="themePreference" className="h-10">
                          <SelectValue placeholder="Select theme" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="system">System</SelectItem>
                          <SelectItem value="light">Light</SelectItem>
                          <SelectItem value="dark">Dark</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Applies across the app and is saved on this device.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="profile" className="mt-0">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <UserCircleIcon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Profile</CardTitle>
                        <CardDescription className="mt-1">
                          Update your personal information and content preferences
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ProfilePreferencesForm
                      profile={profile}
                      onProfileChange={setProfile}
                      preferences={preferences}
                      onPreferencesChange={setPreferences}
                      onSubmit={handleSaveProfile}
                      saving={profileSaving}
                      message={profileMessage}
                      submitLabel="Save changes"
                      savingLabel="Saving…"
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="password" className="mt-0">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <KeyIcon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Change password</CardTitle>
                        <CardDescription className="mt-1">
                          Update your password to keep your account secure
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleChangePassword} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="currentPassword" className="text-sm font-medium">
                          Current password
                        </Label>
                        <Input
                          id="currentPassword"
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          required
                          autoComplete="current-password"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newPassword" className="text-sm font-medium">
                          New password
                        </Label>
                        <Input
                          id="newPassword"
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                          autoComplete="new-password"
                          className="h-10"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Password must be at least 8 characters long
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword" className="text-sm font-medium">
                          Confirm new password
                        </Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          autoComplete="new-password"
                          className="h-10"
                        />
                      </div>
                      {passwordMessage && (
                        <Alert variant={passwordMessage.type === 'success' ? 'success' : 'destructive'} className="mt-4">
                          <AlertDescription>{passwordMessage.text}</AlertDescription>
                        </Alert>
                      )}
                      <div className="flex justify-end pt-2">
                        <Button type="submit" disabled={passwordSaving} size="default">
                          {passwordSaving ? 'Updating…' : 'Update password'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="account" className="mt-0">
                <Card className="border-destructive/50 bg-destructive/5 shadow-sm">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-destructive/20">
                        <TrashIcon className="h-5 w-5 text-destructive" />
                      </div>
                      <div>
                        <CardTitle className="text-lg text-destructive">Delete account</CardTitle>
                        <CardDescription className="mt-1 text-destructive/80">
                          This action cannot be undone. All your data will be permanently deleted.
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleDeleteAccount} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="deleteConfirm" className="text-sm font-medium">
                          Type &quot;delete my account&quot; to confirm
                        </Label>
                        <Input
                          id="deleteConfirm"
                          value={deleteConfirm}
                          onChange={(e) => setDeleteConfirm(e.target.value)}
                          required
                          className="h-10"
                          placeholder="delete my account"
                        />
                      </div>
                      {deleteMessage && (
                        <Alert variant={deleteMessage.type === 'success' ? 'success' : 'destructive'} className="mt-4">
                          <AlertDescription>{deleteMessage.text}</AlertDescription>
                        </Alert>
                      )}
                      <div className="flex justify-end pt-2">
                        <Button
                          type="submit"
                          variant="destructive"
                          disabled={deleteSaving || deleteConfirm !== 'delete my account'}
                          size="default"
                        >
                          {deleteSaving ? 'Deleting…' : 'Delete my account'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
